import { MovimientoCartera } from '../services/carteraService';

const compararFecha = (a: string | undefined, b: string | undefined): number => {
  const timeA = a ? new Date(a).getTime() : 0;
  const timeB = b ? new Date(b).getTime() : 0;
  return timeA - timeB;
};

const importeDebe = (m: MovimientoCartera): number => Number(m.debe) || 0;
const importeHaber = (m: MovimientoCartera): number => Number(m.haber) || 0;

/** Identifica de forma única cada línea expandida del movimiento. */
export const claveMovimientoCartera = (m: MovimientoCartera): string => {
  if (m.nroFormulario === 0) {
    return 'saldo-inicial';
  }
  if (m.nroTipoPago != null && m.nroTipoPago > 0) {
    return `${m.nroFormulario}|tp:${m.nroTipoPago}`;
  }
  return [
    m.nroFormulario,
    m.fechaEmision?.slice(0, 10) ?? '',
    importeDebe(m),
    importeHaber(m),
    m.mcp?.trim() ?? '',
    m.detalle?.trim() ?? '',
    m.tipoPago?.trim() ?? '',
  ].join('|');
};

const compararParaCalculoSaldo = (a: MovimientoCartera, b: MovimientoCartera): number => {
  const cmpFecha = compararFecha(a.fechaEmision, b.fechaEmision);
  if (cmpFecha !== 0) return cmpFecha;
  if (a.nroFormulario !== b.nroFormulario) return a.nroFormulario - b.nroFormulario;
  const debeA = importeDebe(a);
  const debeB = importeDebe(b);
  if (debeA !== debeB) return debeB - debeA;
  const haberA = importeHaber(a);
  const haberB = importeHaber(b);
  if (haberA !== haberB) return haberA - haberB;
  return (a.nroTipoPago ?? 0) - (b.nroTipoPago ?? 0);
};

export const ordenarMovimientosCarteraParaCalculo = (
  movimientos: MovimientoCartera[]
): MovimientoCartera[] =>
  [...movimientos]
    .filter((m) => m.nroFormulario !== 0)
    .sort(compararParaCalculoSaldo);

export const ordenarMovimientosCarteraParaVisualizacion = (
  movimientos: MovimientoCartera[]
): MovimientoCartera[] =>
  [...movimientos].sort((a, b) => {
    const aInicial = a.nroFormulario === 0 ? 1 : 0;
    const bInicial = b.nroFormulario === 0 ? 1 : 0;
    if (aInicial !== bInicial) return aInicial - bInicial;
    return compararParaCalculoSaldo(b, a);
  });

type MovimientoConIndice = {
  mov: MovimientoCartera;
  indice: number;
};

export const aplicarSaldoAcumuladoMovimientosCartera = (
  movimientos: MovimientoCartera[],
  saldoInicial: number
): MovimientoCartera[] => {
  const conIndice: MovimientoConIndice[] = movimientos.map((mov, indice) => ({ mov, indice }));
  const saldoPorIndice = new Map<number, number>();
  let saldo = saldoInicial;

  const paraCalcular = conIndice
    .filter(({ mov }) => mov.nroFormulario !== 0)
    .sort((a, b) => compararParaCalculoSaldo(a.mov, b.mov));

  for (const { mov, indice } of paraCalcular) {
    saldo += importeDebe(mov) - importeHaber(mov);
    saldoPorIndice.set(indice, saldo);
  }

  return conIndice.map(({ mov, indice }) => {
    if (mov.nroFormulario === 0) {
      return { ...mov, saldoAcumulado: saldoInicial };
    }
    const saldoAcumulado = saldoPorIndice.get(indice);
    return saldoAcumulado !== undefined ? { ...mov, saldoAcumulado } : { ...mov, saldoAcumulado: saldoInicial };
  });
};

/** Elimina duplicados al concatenar páginas del API (misma línea con distinto nivel de detalle). */
export const deduplicarMovimientosCartera = (
  movimientos: MovimientoCartera[]
): MovimientoCartera[] => {
  const porClave = new Map<string, MovimientoCartera>();
  const ordenClaves: string[] = [];

  const score = (m: MovimientoCartera) =>
    (m.mcp?.trim() ? 1 : 0) +
    (m.detalle?.trim() ? 1 : 0) +
    (m.tipoPago?.trim() ? 1 : 0) +
    (m.nroTipoPago != null ? 2 : 0);

  for (const mov of movimientos) {
    const clave = claveMovimientoCartera(mov);
    const existente = porClave.get(clave);
    if (!existente) {
      porClave.set(clave, mov);
      ordenClaves.push(clave);
      continue;
    }
    if (score(mov) > score(existente)) {
      porClave.set(clave, mov);
    }
  }

  return ordenClaves
    .map((clave) => porClave.get(clave))
    .filter((mov): mov is MovimientoCartera => mov != null);
};
